import { IBet, IEvent, IEventScoreBoard, IEventScoreBoardResult, ILocation, IParticipant, ITournament } from "bets.oddprovider.lib.common.ts/models"
import { ApiClientUtils } from "fwork-jsts-common/src/apiClient"
import { FormItemStyled, FormStyled } from "fwork.react.apptemplate"
import moment from 'moment'
import { useSnackbar } from "notistack"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import axios from "../../apiClient/axios"
import { EventsApiClient } from "../../apiClient/events"
import { LocationsApiClient } from "../../apiClient/locations"
import { TournamentsApiClient } from "../../apiClient/tournaments"
import { DEFAULTGAP } from "../../globals"
import { IDotEnvProductionState } from "../../redux/reducers/dotenvProduction.slice"
import { RootState } from "../../redux/store"
import { DateTimePickerExtComponent } from "../common/datePicker"
import { DropDownSearchComponent } from "../common/dropDownSearch"
import { FloatActionButton } from "../common/floatActionButton"
import { UserAddInfoTabsComponent } from "./eventAddInfoTabs"
import { EventSportComponent } from "./eventSport"
import { EventStatusComponent } from "./evetStatus"

export const EventComponent = (props: {
  id?: number
}) => {

  const [data, setData] = useState<IEvent>()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const dotEnvProductionState: IDotEnvProductionState = useSelector((rootState: RootState) => rootState.dotEnvProductionState)

  let gettingData = false
  const getData = async () => {
    if (gettingData) return
    gettingData = true

    try {
      const response = await new EventsApiClient(axios).get({
        where: {
          id: Number(props.id)
        },
        exclude: '_id',
        nested: 'status,location,sport',
        sort: { name: 1 }
      })

      if (response.success && response?.data?.payload) {
        setData(response.data.payload[0])
      }
    } catch (error) {
      enqueueSnackbar(ApiClientUtils.getErrorMessage(error), { variant: 'error' })
    } finally {
      gettingData = false
    }
  }

  const [gettingTournaments, setGettingTournaments] = useState<boolean>(false)
  const getTournaments = async (filter: string) => {
    if (gettingTournaments) return
    setGettingTournaments(true)

    try {
      const response = await new TournamentsApiClient(axios).get({
        where: {
          name: { $regex: filter, $options: 'i' }
        },
        sort: { name: 1 }
      })
      return response.data?.payload
    } catch (error) {
      enqueueSnackbar(ApiClientUtils.getErrorMessage(error), { variant: 'error' })
    } finally {
      setGettingTournaments(false)
    }
  }

  const [gettingLocations, setGettingLocations] = useState<boolean>(false)
  const getLocations = async (filter: string) => {
    if (gettingLocations) return
    setGettingLocations(true)

    try {
      const response = await new LocationsApiClient(axios).get({
        where: {
          name: { $regex: filter, $options: 'i' }
        },
        sort: { name: 1 }
      })
      return response.data?.payload
    } catch (error) {
      enqueueSnackbar(ApiClientUtils.getErrorMessage(error), { variant: 'error' })
    } finally {
      setGettingLocations(false)
    }
  }

  const [saving, setSaving] = useState<boolean>(false)
  const save = async () => {
    if (saving) return
    setSaving(true)
    try {
      if (data) {
        const response =
          data.id ? await new EventsApiClient(axios).put({ data }) : await new EventsApiClient(axios).post({ data })
        if (response.success) {
          if (response.data?.id)
            data.id = response.data?.id
          enqueueSnackbar('Alterações salvas com sucesso.', { variant: 'success' })
        }
        else
          enqueueSnackbar(response.msg || 'Erro ao salvar alterações.', { variant: 'error', style: { whiteSpace: 'pre-line' } })
      }
    } catch (error) {
      enqueueSnackbar(ApiClientUtils.getErrorMessage(error), { variant: 'error', style: { whiteSpace: 'pre-line' } })
    }
    finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (props.id)
      getData()
    else setData({} as IEvent)
  }, [])

  useEffect(() => {
    if (props.id)
      getData()
    else setData({} as IEvent)
  }, [dotEnvProductionState.payload?.APIURL])

  return <>
    <FormStyled gap={DEFAULTGAP}>
      <EventSportComponent data={data} onChange={(event) => setData({
        ...data,
        sport_id: Number(event.target.value),
      } as IEvent)} />

      <FormItemStyled gap={DEFAULTGAP}>
        <DropDownSearchComponent
          initInputValue={data?.tournament?.name || ''}
          progress={gettingTournaments}
          label="Torneio"
          modelLabelName="name"
          dataBuilder={async (typedText: string | undefined) => {
            if (typedText)
              return await getTournaments(typedText)
          }}
          onSelectItem={(item: ITournament) => {
            setData({
              ...data,
              tournament: item,
            } as IEvent)
          }}
          onClear={() => setData({
            ...data,
            tournament: {},
          } as IEvent)}
        />
      </FormItemStyled>

      <EventStatusComponent data={data} onChange={(event) => setData({
        ...data,
        status_id: Number(event.target.value),
      } as IEvent)} />

      <FormItemStyled gap={DEFAULTGAP}>
        <DropDownSearchComponent
          initInputValue={data?.location?.name || ''}
          progress={gettingLocations}
          label="Localização"
          modelLabelName="name"
          dataBuilder={async (typedText: string | undefined) => {
            if (typedText)
              return await getLocations(typedText)
          }}
          onSelectItem={(item: ILocation) => {
            setData({
              ...data,
              location_id: item.id,
            } as IEvent)
          }}
          onClear={() => setData({
            ...data,
            location_id: Number(undefined),
          } as IEvent)}
        />
      </FormItemStyled>

      <FormItemStyled gap={DEFAULTGAP}>
        <DateTimePickerExtComponent
          ampm={false}
          label="Data"
          value={moment(data?.start_date) || null}
          onChange={(value: any) => {
            setData({
              ...data,
              start_date: value
            } as IEvent)
          }}
        />
      </FormItemStyled>
    </FormStyled>

    <UserAddInfoTabsComponent
      participantsProps={{
        participants: data?.participants || [],
        onSelectItem: (item: IParticipant, oldItem: IParticipant) => {
          let participantsTmp = [...data?.participants || []]
          for (let i = 0; i < participantsTmp.length; i++) {
            var p = participantsTmp[i]
            if (p.id == oldItem.id)
              participantsTmp[i] = item
          }
          setData({
            ...data,
            participants: participantsTmp,
          } as IEvent)
        },
        onDelete: (item: IParticipant) => {
          setData({
            ...data,
            participants: data?.participants.filter(p => p.id != item.id)
          } as IEvent)
        },
        onAdd: () => {
          let participantsTmp = [...data?.participants || []]

          if (participantsTmp?.filter(p => p.id == 0).length)
            return

          participantsTmp.push({
            id: 0,
            name: ''
          })
          setData({
            ...data,
            participants: participantsTmp
          } as IEvent)
        }
      }}

      betsProps={data?.id ? {
        eventId: data?.id,
        data: data.bets,
        onLoadData: (bets?: IBet[] | undefined) => {
          setData({
            ...data,
            bets,
          })
        },
        onChangeItem: (bet: IBet) => {
          setData(prev => {
            let tmp = {
              ...prev
            } as IEvent

            const itemIndex = prev?.bets?.findIndex(b => b.id === bet.id);
            if ((itemIndex ?? -1) > -1) {
              const itemsCopy = prev?.bets?.slice();
              if (itemsCopy?.length) {
                itemsCopy[itemIndex!] = bet
              }

              tmp.bets = itemsCopy
            }

            return tmp
          });
        }
      } : undefined}

      scoreProps={{
        scores: data?.score_board || [],
        onChangeItem: (oldItem: IEventScoreBoard, newItem: IEventScoreBoard) => {
          setData(prev => {
            let tmp = {
              ...prev
            } as IEvent

            const itemIndex = prev?.score_board?.findIndex(b => b.id === oldItem.id);
            if ((itemIndex ?? -1) > -1) {
              const itemsCopy = prev?.score_board?.slice();
              if (itemsCopy?.length) {
                itemsCopy[itemIndex!] = newItem
              }

              tmp.score_board = itemsCopy ?? []
            }

            return tmp
          });
        },
        onDelete: (item: IParticipant) => {
          setData({
            ...data,
            score_board: data?.score_board?.filter(s => s.id != item.id)
          } as IEvent)
        },
        onAdd: () => {
          let scoresTmp = [...data?.score_board || []]

          if (scoresTmp?.filter(p => p.id == 0).length)
            return

          scoresTmp.push({
            id: 0,
            score_type_id: 0,
            name: '',
            result: [{
              position: 1,
              value: ''
            }, {
              position: 2,
              value: ''
            }],
          })
          setData({
            ...data,
            score_board: scoresTmp
          } as IEvent)
        }
      }}

      scoreBoardProps={data ? {
        event: data,
        onChangeResult: (eventScoreBoard, result) => {
          const dataTmp = {
            ...data
          }
          if (!dataTmp.score_board?.length)
            dataTmp.score_board = []
          let dataScoreBoard = dataTmp.score_board.filter(s => s.score_type_id == eventScoreBoard.score_type_id)[0]
          if (!dataScoreBoard) {
            dataScoreBoard = {
              id: 0,
              score_type_id: eventScoreBoard.score_type_id,
              name: eventScoreBoard.name,
              result: dataTmp?.participants?.map((p, idx) => ({ position: idx + 1 } as IEventScoreBoardResult)) ?? [],
            } as IEventScoreBoard
            dataTmp.score_board.push(dataScoreBoard)
          }
          dataScoreBoard.result = dataScoreBoard.result.map(r => (r.position == result.position ? result : r))

          setData(dataTmp)
        }
      } : undefined}
    />

    <FloatActionButton progress={saving} onCancelClick={() => navigate(-1)} onConfirmClick={save} />
  </>
}